// frontend/src/components/TopNav.js
import React from 'react';
import {
    Box,
    Button,
    HStack,
    Text,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Avatar,
    Icon,
    IconButton,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import {
    MdChat,
    MdDescription,
    MdSettings,
    MdLogout,
    MdLock,
    MdPerson,
    MdGroups,
    MdVpnKey,
} from 'react-icons/md';
import WorkspaceSwitcher, { MobileWorkspaceSwitcher } from './WorkspaceSwitcher';

// ─── Desktop: floating pill nav (unchanged from original) ──────────────────────

// `adminWithoutWorkspace`: a platform superuser holding no membership. Chat,
// Documents and the workspace switcher all need a workspace, so they are hidden
// rather than left as controls that lead nowhere. The admin entries stay --
// they are the way out of this state. See the WorkspaceGate note in App.js.
const DesktopNav = ({ currentView, onViewChange, user, onChangePassword, onLogout, adminWithoutWorkspace = false }) => (
    <Box
        display={{ base: 'none', md: 'block' }}
        position="fixed"
        top="10px"
        left="50%"
        transform="translateX(-50%)"
        zIndex="popover"
        pointerEvents="none"
    >
        <HStack
            spacing={0}
            bg="white"
            border="1px"
            borderColor="gray.200"
            borderRadius="full"
            boxShadow="sm"
            pointerEvents="auto"
            overflow="hidden"
        >
            {!adminWithoutWorkspace && (
                <Button
                    size="sm"
                    variant={currentView === 'chat' ? 'solid' : 'ghost'}
                    colorScheme="brand"
                    leftIcon={<Icon as={MdChat} boxSize="16px" />}
                    onClick={() => onViewChange('chat')}
                    fontSize="sm"
                    px={4}
                    borderRadius="0"
                >
                    Chat
                </Button>
            )}
            {!adminWithoutWorkspace && (
                <Button
                    size="sm"
                    variant={currentView === 'documents' ? 'solid' : 'ghost'}
                    colorScheme="brand"
                    leftIcon={<Icon as={MdDescription} boxSize="16px" />}
                    onClick={() => onViewChange('documents')}
                    fontSize="sm"
                    px={4}
                    borderRadius="0"
                >
                    Documents
                </Button>
            )}

            {/* Which workspace am I writing into? Must be visible before typing.
                With no membership there is nothing to switch between. */}
            {!adminWithoutWorkspace && <WorkspaceSwitcher />}

            <Menu>
                <MenuButton
                    as={Button}
                    size="sm"
                    variant="ghost"
                    rightIcon={<ChevronDownIcon />}
                    borderRadius="0"
                    px={3}
                >
                    <HStack spacing={2}>
                        <Avatar
                            size="xs"
                            name={user?.full_name || user?.username}
                            bg="corporate.secondary"
                            color="white"
                        />
                        <Text fontSize="sm">
                            {user?.full_name || user?.username}
                        </Text>
                    </HStack>
                </MenuButton>
                <MenuList>
                    <MenuItem icon={<Icon as={MdLock} />} onClick={onChangePassword}>
                        Change Password
                    </MenuItem>
                    {user?.is_admin && (
                        <MenuItem
                            icon={<Icon as={MdSettings} />}
                            onClick={() => onViewChange('users')}
                        >
                            User Management
                        </MenuItem>
                    )}
                    {user?.is_admin && (
                        <MenuItem
                            icon={<Icon as={MdGroups} />}
                            onClick={() => onViewChange('workspaces')}
                        >
                            Workspaces
                        </MenuItem>
                    )}
                    {/* Platform admin only (Steph's option A): provider API keys
                        decide who pays, so this is not delegated to workspace
                        admins. Same is_admin guard as the views above. */}
                    {user?.is_admin && (
                        <MenuItem
                            icon={<Icon as={MdVpnKey} />}
                            onClick={() => onViewChange('credentials')}
                        >
                            Credentials
                        </MenuItem>
                    )}
                    <MenuItem icon={<Icon as={MdLogout} />} onClick={onLogout}>
                        Logout
                    </MenuItem>
                </MenuList>
            </Menu>
        </HStack>
    </Box>
);

// ─── Mobile: bottom tab bar ────────────────────────────────────────────────────
// Sits at the very bottom, below MessageInput.
// Thumb-reachable, no overlap with the hamburger button (top-left) or
// the scroll-to-bottom FAB (bottom-right, positioned above this bar).

const MobileNav = ({ currentView, onViewChange, user, onChangePassword, onLogout, adminWithoutWorkspace = false }) => (
    <Box
        display={{ base: 'flex', md: 'none' }}
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        zIndex="sticky"
        bg="white"
        borderTop="1px"
        borderColor="gray.200"
        px={1}
        py={1}
        justifyContent="space-around"
        alignItems="center"
        // Safe area for phones with gesture bars / home indicators
        pb="calc(4px + env(safe-area-inset-bottom))"
    >
        {!adminWithoutWorkspace && (
            <IconButton
                aria-label="Chat"
                icon={<Icon as={MdChat} boxSize="20px" />}
                variant="ghost"
                colorScheme={currentView === 'chat' ? 'brand' : 'gray'}
                size="sm"
                onClick={() => onViewChange('chat')}
                flexDirection="column"
                h="auto"
                py={1}
                minW="60px"
            />
        )}
        {!adminWithoutWorkspace && (
            <IconButton
                aria-label="Documents"
                icon={<Icon as={MdDescription} boxSize="20px" />}
                variant="ghost"
                colorScheme={currentView === 'documents' ? 'brand' : 'gray'}
                size="sm"
                onClick={() => onViewChange('documents')}
                flexDirection="column"
                h="auto"
                py={1}
                minW="60px"
            />
        )}

        {/* Scope must be visible on mobile too -- see WorkspaceSwitcher.js. */}
        {!adminWithoutWorkspace && <MobileWorkspaceSwitcher />}

        <Menu placement="top">
            <MenuButton
                as={IconButton}
                aria-label="Account"
                icon={
                    <Avatar
                        size="xs"
                        name={user?.full_name || user?.username}
                        bg="corporate.secondary"
                        color="white"
                    />
                }
                variant="ghost"
                size="sm"
                h="auto"
                py={1}
                minW="60px"
            />
            <MenuList>
                <MenuItem isDisabled fontSize="sm" fontWeight="600">
                    {user?.full_name || user?.username}
                </MenuItem>
                <MenuItem icon={<Icon as={MdLock} />} onClick={onChangePassword}>
                    Change Password
                </MenuItem>
                {user?.is_admin && (
                    <MenuItem
                        icon={<Icon as={MdSettings} />}
                        onClick={() => onViewChange('users')}
                    >
                        User Management
                    </MenuItem>
                )}
                {user?.is_admin && (
                    <MenuItem
                        icon={<Icon as={MdGroups} />}
                        onClick={() => onViewChange('workspaces')}
                    >
                        Workspaces
                    </MenuItem>
                )}
                {/* Mirrors DesktopNav: platform admin only. */}
                {user?.is_admin && (
                    <MenuItem
                        icon={<Icon as={MdVpnKey} />}
                        onClick={() => onViewChange('credentials')}
                    >
                        Credentials
                    </MenuItem>
                )}
                <MenuItem icon={<Icon as={MdLogout} />} onClick={onLogout}>
                    Logout
                </MenuItem>
            </MenuList>
        </Menu>
    </Box>
);

// ─── Combined component ────────────────────────────────────────────────────────

const TopNav = (props) => (
    <>
        <DesktopNav {...props} />
        <MobileNav {...props} />
    </>
);

export default TopNav;